import {
  CheckCircleTwoTone,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  ImportOutlined,
  LockOutlined,
  PlusOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { shopApi } from "api/shop.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
  UserCell,
} from "components/table";
import { observer } from "mobx-react";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { areaStore } from "store/areaStore";
import { businessTypeStore } from "store/businessTypeStore";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Shop, ShopType } from "types/shop";
import { getTitle } from "utils";
import { exportExcel } from "utils/exportExcel";
import { $url } from "utils/url";
import { ImportShopModal } from "views/ShopNotApproved/components/ImportShopModal";
import { SubmitHistory } from "views/ShopNotApproved/components/SubmitHistory";
import { ShopModal } from "./components/ShopModal";
import { SuspendModal } from "./components/SuspendModal";
import { PassportCheckHistories } from "views/Passport/components/CheckPassportHistories";
import type { Key } from "react";

interface PageQueryParam extends QueryParam {
  areaId: number | null;
  businessTypeId: number | null;
  isHasAccount: boolean | null;
  isApproved?: boolean;
}

export const ShopPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [businessTypeId, setBusinessTypeId] = useState<string | number>("");
  const [isHasAccount, setIsHasAccount] = useState<string | number>("");

  const [query, setQuery] = useState<PageQueryParam>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
    businessTypeId: null,
    isHasAccount: null,
    isApproved: true,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Shop[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleSuspendModal, setVisibleSuspendModal] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Partial<Shop>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [shopIds, setShopIds] = useState<number[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [visibleImport, setVisibleImport] = useState(false);
  const [visibleSubmitHistory, setVisibleSubmitHistory] = useState(false);
  const [visiblePassportCheckHistory, setVisiblePassportCheckHistory] =
    useState(false);

  useEffect(() => {
    document.title = getTitle(title);
    areaStore.fetchList();
    businessTypeStore.fetchList();
  }, [title]);

  const fetchData = useCallback(async (queryParams: PageQueryParam) => {
    setLoading(true);
    try {
      const res = await shopApi.findAll(queryParams);
      setData(res.data.shops);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleDelete = useCallback(
    async (row: Shop) => {
      setLoading(true);
      try {
        await shopApi.delete(row.id);
        fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const handleSuspend = () => {
    setShopIds(selectedRowKeys.map((e) => Number(e)));
    setVisibleSuspendModal(true);
  };

  const handleUnsuspend = useCallback(
    async (record: Shop) => {
      try {
        setLoading(true);
        await shopApi.unSuspend(record.id);
      } finally {
        setLoading(false);
        fetchData(query);
      }
    },
    [fetchData, query],
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const generateBusiness = (shopType: ShopType) => {
    switch (shopType) {
      case ShopType.Company:
        return "Công ty";
      case ShopType.Retail:
        return "Bán lẻ";
      case ShopType.Booking:
        return "Đặt lịch, nhà hàng";
      case ShopType.BookCar:
        return "Đặt xe";
    }

    //    Company = "COMPANY", //Default
    // Retail = "RETAIL", //bán lẻ
    // Booking = "BOOKING", //Đặt lịch
    // BookCar = "BOOKCAR", //Đặt xe
  };

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  const handleBusinessTypeChange = useCallback(
    (value: string | number | undefined) => {
      setBusinessTypeId(value ?? "");
      setQuery((prev) => ({
        ...prev,
        page: 1,
        businessTypeId: value ? Number(value) : null,
      }));
    },
    [],
  );

  const handleAccountStatusChange = useCallback(
    (value: string | number | undefined) => {
      setIsHasAccount(value ?? "");
      setQuery((prev) => ({
        ...prev,
        page: 1,
        isHasAccount:
          value === "all" || value === undefined ? null : value === "true",
      }));
    },
    [],
  );

  const handleExport = async () => {
    const { limit: _limit, page: _page, ...queryExport } = query;
    const res = await shopApi.findAll(queryExport);

    const shopData: Shop[] = res.data.shops;

    const orderDataExport = shopData.map((shop: Shop) => {
      const result: any = {
        "Mã số doanh nghiệp": shop.code,
        "Tên doanh nghiệp": shop.name,
        "Địa chỉ": shop.address,
        // "Số điện thoại": shop.phone,
        // Email: shop.email,
        Website: shop.website,
        "Lĩnh vực kinh doanh": shop.businessArea,
        "Loại hình kinh doanh": shop?.businessType?.name,
        "Loại doanh nghiệp": generateBusiness(shop.type),
        "Tên ngân hàng": shop.bankName,
        "STK ngân hàng": shop.bankNumber,
        "Tên chủ tài khoản": shop.bankOwner,
        "Tên tài khoản đăng nhập": shop.username,
        "Tên thành viên": shop?.customer?.firstName,
        "Số điện thoại": shop?.customer?.phone,
        Email: shop?.customer?.email,
        "Trạng thái": shop.isSuspended ? "Bị khóa" : "Đang hoạt động",
      };

      return result;
    });

    exportExcel(orderDataExport, "Danh sach doanh nghiep");
  };

  const columns = useMemo<TableColumnsType<Shop>>(
    () => [
      {
        title: "STT",
        key: "position",
        width: 70,
        render: (_, __, index) => <div>{(query.page - 1) * query.limit + index + 1}</div>,
      },
      {
        title: "Tên doanh nghiệp",
        dataIndex: "name",
        key: "name",
        width: 330,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <INA
              image={record?.avatar ? $url(record.avatar) : undefined}
              name={record.name || "--"}
              additional={`Mã: ${record.code || "--"}`}
            />
            {!!record.username && (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <CheckCircleTwoTone twoToneColor="#52c41a" />
                <span>Đã đăng ký tài khoản</span>
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Thông tin thành viên",
        key: "customerInfo",
        width: 270,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <UserCell
              name={`${record?.customer?.firstName} ${record?.customer?.lastName}`}
              avatar={record?.customer?.avatar ? $url(record.customer.avatar) : undefined}
              email={record?.customer?.phone}
            />
          </div>
        ),
      },
      {
        title: "Lĩnh vực kinh doanh",
        key: "shopInfo",
        width: 300,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <TableContent>{record?.businessType?.name || "Chưa phân loại"}</TableContent>
          </div>
        ),
      },
      {
        title: "Thao tác",
        key: "action",
        width: 140,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Chỉnh sửa">
              <ActionButton
                aria-label="Chỉnh sửa"
                onClick={() => {
                  setSelectedShop(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>

            <Tooltip title="Xóa">
              <Popconfirm
                title="Xác nhận xóa?"
                cancelText="Không"
                okText="Xóa"
                onConfirm={() => handleDelete(record)}
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>

            {record.isSuspended ? (
              <Tooltip title="Mở khóa">
                <Popconfirm title="Mở khóa?" onConfirm={() => handleUnsuspend(record)}>
                  <ActionButton aria-label="Mở khóa">
                    <UnlockOutlined style={{ color: "#2563eb" }} />
                  </ActionButton>
                </Popconfirm>
              </Tooltip>
            ) : (
              <Tooltip title="Khóa">
                <Popconfirm
                  title="Khóa doanh nghiệp?"
                  onConfirm={() => {
                    setVisibleSuspendModal(true);
                    setShopIds([record.id]);
                  }}
                >
                  <ActionButton aria-label="Khóa">
                    <LockOutlined style={{ color: "#ef4444" }} />
                  </ActionButton>
                </Popconfirm>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [handleDelete, handleUnsuspend, query.limit, query.page],
  );

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  const businessTypeOptions = businessTypeStore.list.map((e) => ({
    value: e.id,
    label: e.name,
  }));

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm tên...",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedShop({});
                  }}
                >
                  Thêm mới doanh nghiệp
                </AppButton>
              }
              filters={[
                {
                  key: "businessTypeId",
                  value: businessTypeId,
                  placeholder: "Khu",
                  width: 200,
                  options: businessTypeOptions,
                  allowClear: true,
                  showSearch: true,
                  filterOption: false,
                  onChange: handleBusinessTypeChange,
                },
                {
                  key: "isHasAccount",
                  value: isHasAccount,
                  placeholder: "Tình trạng nhà xưởng...",
                  width: 200,
                  options: [
                    { value: "true", label: "Đã đăng ký tài khoản" },
                    { value: "false", label: "Chưa đăng ký tài khoản" },
                  ],
                  allowClear: true,
                  onChange: handleAccountStatusChange,
                },
              ]}
              actions={
                <>
                  <AppButton
                    tone="light"
                    icon={<LockOutlined />}
                    onClick={handleSuspend}
                    disabled={!selectedRowKeys.length}
                    loading={loading}
                  >
                    Khóa
                  </AppButton>
                  <AppButton
                    tone="primary"
                    icon={<ImportOutlined />}
                    onClick={() => setVisibleImport(true)}
                  >
                    Import Excel
                  </AppButton>
                  <Popconfirm
                    title="Bạn có muốn xuất thông tin doanh nghiệp ra file excel"
                    okText="Xuất file excel"
                    onConfirm={handleExport}
                  >
                    <AppButton tone="primary" icon={<ExportOutlined />}>
                      Xuất file Excel
                    </AppButton>
                  </Popconfirm>
                </>
              }
            />
          }
          table={
            <AppTable<Shop>
              rowKey="id"
              loading={loading}
              pagination={false}
              dataSource={data}
              columns={columns}
              rowSelection={rowSelection}
              scroll={{ x: 1200 }}
            />
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              totalLabel="doanh nghiệp"
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                setQuery((prev) => ({
                  ...prev,
                  page,
                  limit,
                }));
              }}
            />
          }
        />

      {visibleModal && (
        <ShopModal
          onSubmitOk={() => {
            fetchData(query);
            setSelectedShop({});
            setVisibleModal(false);
          }}
          onClose={() => {
            console.log("onclose ne");
            setSelectedShop({});
            setVisibleModal(() => false);
          }}
          isModalVisible={visibleModal}
          shop={selectedShop}
          status={modalStatus}
        />
      )}

      <ImportShopModal
        visible={visibleImport}
        onClose={() => setVisibleImport(false)}
        onSubmitOk={() =>
          setTimeout(() => {
            fetchData(query);
          }, 2000)
        }
      />
      <SuspendModal
        visible={visibleSuspendModal}
        shopIds={shopIds}
        status={"create"}
        onClose={() => setVisibleSuspendModal(false)}
        onSubmitOk={() => {
          fetchData(query);
          setSelectedRowKeys([]);
        }}
      />
      {visibleSubmitHistory && (
        <SubmitHistory
          visible={visibleSubmitHistory}
          selectedShop={selectedShop}
          onCancel={() => setVisibleSubmitHistory(false)}
        />
      )}
      {visiblePassportCheckHistory && (
        <PassportCheckHistories
          visible={visiblePassportCheckHistory}
          selectedPassport={selectedShop}
          onCancel={() => setVisiblePassportCheckHistory(false)}
        />
      )}
    </div>
  );
});
